/**
 * @file Sort control component for column headers
 * @description Clickable column headers with sort indicators for multi-field sorting
 */

export default {
  name: 'SortControl',

  props: {
    schema: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      default: null, // null = use all schema properties
    },
    excludeFields: {
      type: Array,
      default: () => [],
    },
    sort: {
      type: Array,
      default: () => [],
    },
    actionControlStyles: {
      type: Object,
      default: () => ({}),
    },
    fieldHeaderStyles: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:sort'],

  computed: {
    /**
     * Get the item schema (handles array schema with items property)
     */
    itemSchema() {
      return this.schema.items || this.schema;
    },

    /**
     * Get visible field names based on fields and excludeFields props
     */
    visibleFields() {
      if (this.itemSchema.type !== 'object' || !this.itemSchema.properties) {
        return [];
      }

      const allFields = Object.keys(this.itemSchema.properties);

      if (this.fields) {
        return this.fields.filter(f => allFields.includes(f));
      }

      return allFields.filter(f => !this.excludeFields.includes(f));
    },

    /**
     * Map of field -> sort info { direction: 'asc'|'desc', priority: number }
     */
    sortMap() {
      const map = {};
      this.sort.forEach((item, index) => {
        if (typeof item === 'string') {
          map[item] = { direction: 'asc', priority: index + 1 };
        } else {
          const field = Object.keys(item)[0];
          map[field] = { direction: item[field], priority: index + 1 };
        }
      });
      return map;
    },

    /**
     * Whether multi-sort is active (more than one field sorted)
     */
    isMultiSort() {
      return this.sort.length > 1;
    },
  },

  methods: {
    /**
     * Get field title from schema
     * @param {string} fieldName - Field name
     * @returns {string} Field title or name
     */
    getFieldTitle(fieldName) {
      const fieldSchema = this.itemSchema.properties?.[fieldName];
      return fieldSchema?.title || fieldName;
    },

    /**
     * Check if a field is sortable (primitive type)
     * @param {string} fieldName - Field name
     * @returns {boolean} True if field is sortable
     */
    isSortable(fieldName) {
      const fieldSchema = this.itemSchema.properties?.[fieldName];
      const type = fieldSchema?.type;
      return type !== 'object' && type !== 'array';
    },

    /**
     * Get sort state for a field
     * @param {string} fieldName - Field name
     * @returns {object | null} Sort state { direction, priority } or null
     */
    getSortState(fieldName) {
      return this.sortMap[fieldName] || null;
    },

    /**
     * Handle column header click
     * Single-click: replace entire sort
     * Shift+click: add to/modify multi-sort
     * @param {string} fieldName - Field name
     * @param {MouseEvent} event - Click event
     */
    handleClick(fieldName, event) {
      const isMultiSort = event.shiftKey;
      const currentState = this.getSortState(fieldName);
      let newSort;

      if (isMultiSort) {
        // Multi-sort mode: add to/modify existing sort
        newSort = [...this.sort];
        const existingIndex = newSort.findIndex(item => {
          const field = typeof item === 'string' ? item : Object.keys(item)[0];
          return field === fieldName;
        });

        if (existingIndex === -1) {
          // Not sorted -> ascending
          newSort.push(fieldName);
        } else if (currentState.direction === 'asc') {
          // Ascending -> descending
          newSort[existingIndex] = { [fieldName]: 'desc' };
        } else {
          // Descending -> remove
          newSort.splice(existingIndex, 1);
        }
      } else {
        // Single-sort mode: replace entire sort
        if (!currentState) {
          // Not sorted -> ascending
          newSort = [fieldName];
        } else if (currentState.direction === 'asc') {
          // Ascending -> descending
          newSort = [{ [fieldName]: 'desc' }];
        } else {
          // Descending -> clear sort
          newSort = [];
        }
      }

      this.$emit('update:sort', newSort);
    },
  },

  template: `
    <!-- Empty cell for action column -->
    <div class="fw-bold" :style="actionControlStyles"></div>

    <!-- Column headers -->
    <div
      v-for="fieldName in visibleFields"
      :key="'sort-header-' + fieldName"
      class="fw-bold d-flex align-items-center gap-1"
      :class="{ 'user-select-none': isSortable(fieldName) }"
      :style="{ ...fieldHeaderStyles, cursor: isSortable(fieldName) ? 'pointer' : 'default' }"
      @click="isSortable(fieldName) && handleClick(fieldName, $event)"
    >
      <span>{{ getFieldTitle(fieldName) }}</span>

      <!-- Sort indicator (only for sortable/primitive fields) -->
      <template v-if="isSortable(fieldName)">
        <span v-if="!getSortState(fieldName)" class="text-muted opacity-50">
          <i class="bi bi-arrow-down-up"></i>
        </span>
        <span v-else-if="getSortState(fieldName).direction === 'asc'" class="text-primary">
          <i class="bi bi-arrow-up"></i>
          <small v-if="isMultiSort" class="ms-1">{{ getSortState(fieldName).priority }}</small>
        </span>
        <span v-else class="text-primary">
          <i class="bi bi-arrow-down"></i>
          <small v-if="isMultiSort" class="ms-1">{{ getSortState(fieldName).priority }}</small>
        </span>
      </template>
    </div>
  `,
};
